/** What a single map cell is made of. Tiles are data; the renderer is generic. */
export interface TileDef {
  id: string;
  /** Colour of the top face. */
  color: number;
  /** Colour of the sides; defaults to a darkened `color`. */
  sideColor?: number;
  /** Height of the block in world units. 0 is flush floor. */
  height?: number;
  /** Blocks movement. */
  solid?: boolean;
  /** Scales the map's base encounter rate. 0 disables encounters here. */
  encounterScale?: number;
  /** Bobs up and down — water, lava, that sort of thing. */
  animated?: boolean;
  /** Procedural scenery placed on the tile. */
  prop?: "tree" | "pine" | "rock" | "flower" | "sign" | "torch" | "grave" | "crystal";
  /** Prop colour override. */
  propColor?: number;
  /** Plays this footstep sound instead of the default. */
  step?: string;
}

export class TileSet {
  private tiles = new Map<string, TileDef>();

  constructor(defs: readonly TileDef[] = []) {
    for (const def of defs) this.add(def);
  }

  add(def: TileDef): this {
    this.tiles.set(def.id, def);
    return this;
  }

  get(id: string): TileDef {
    const def = this.tiles.get(id);
    if (!def) throw new Error(`Unknown tile: ${id}`);
    return def;
  }

  has(id: string): boolean {
    return this.tiles.has(id);
  }

  all(): TileDef[] {
    return [...this.tiles.values()];
  }
}

/** Darkens a hex colour by `amount` (0..1) for side faces and shading. */
export function shade(color: number, amount: number): number {
  const r = Math.round(((color >> 16) & 0xff) * (1 - amount));
  const g = Math.round(((color >> 8) & 0xff) * (1 - amount));
  const b = Math.round((color & 0xff) * (1 - amount));
  return (r << 16) | (g << 8) | b;
}
